import {
  approximateUsd,
  changePersonId,
  formatMoney,
  isSquadChange,
  type Change,
  type ChangeKind,
} from '@tive/core';
import { X } from 'lucide-react';
import { useState } from 'react';
import type { ColumnDef } from '../lib/table';
import { cn } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';
import { DataTable } from './DataTable';

/**
 * Every category a change can be in, in the order the panel lists them.
 *
 * Duplicated from `ChangesList` on purpose: the panel groups by kind to be read
 * top to bottom, and this picks by kind to decide what leaves the app. Sharing
 * one list would tie the labels on a export to the icons in a sidebar.
 */
const CATEGORIES: { kind: ChangeKind; label: string }[] = [
  { kind: 'move', label: 'Reporting' },
  { kind: 'hire', label: 'New roles' },
  { kind: 'retitle', label: 'Titles' },
  { kind: 'department', label: 'Departments' },
  { kind: 'location', label: 'Locations' },
  { kind: 'dotted', label: 'Dotted lines' },
  { kind: 'departure', label: 'Departures' },
  { kind: 'squad-add', label: 'New teams' },
  { kind: 'squad-member', label: 'Team moves' },
  { kind: 'squad-rename', label: 'Team renames' },
  { kind: 'squad-parent', label: 'Team nesting' },
  { kind: 'squad-disband', label: 'Teams standing down' },
];

/**
 * The whole change list at once, as a table, over the app.
 *
 * The panel is a column six hundred pixels wide, which is the right shape for
 * working and the wrong one for reading thirty changes or for handing them to
 * somebody. This is the same changes with room, and the way they leave the app.
 */
export function ChangesTable() {
  const changes = useAppStore((s) => s.changes);
  const scenarioName = useAppStore((s) => s.scenario?.name);
  const setChangesTableOpen = useAppStore((s) => s.setChangesTableOpen);
  const select = useAppStore((s) => s.select);
  const openPanel = useAppStore((s) => s.openPanel);
  const revealPerson = useAppStore((s) => s.revealPerson);

  /*
   * Which categories are included, and it starts as all of them.
   *
   * Not persisted, unlike the columns: a table somebody arranged is how they
   * like to work, but which categories to send is a decision about *this*
   * export — and silently leaving a category out of the next one because of a
   * choice made last month is how a plan gets sent with a piece missing.
   */
  const [only, setOnly] = useState<Set<ChangeKind>>(new Set());
  const chosen = only.size === 0 ? null : only;

  const present = CATEGORIES.filter((category) => changes.some((change) => change.kind === category.kind));
  const rows = chosen ? changes.filter((change) => chosen.has(change.kind)) : changes;

  const toggle = (kind: ChangeKind) => {
    const next = new Set(only.size === 0 ? present.map((category) => category.kind) : only);
    if (next.has(kind)) next.delete(kind);
    else next.add(kind);
    // Back to every category rather than none, because a table showing nothing
    // is not a state anybody was asking for.
    setOnly(next.size === present.length || next.size === 0 ? new Set() : next);
  };

  return (
    /*
     * A dialog, and modal, because it covers everything: without saying so, the
     * view underneath stays in the accessibility tree, and a screen reader walks
     * into a second table nobody can see.
     */
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Changes"
      className="fixed inset-0 z-40 flex flex-col bg-[var(--color-canvas)]"
    >
      <div className="flex items-center gap-3 border-b border-[var(--color-line)] px-3 py-2.5">
        <h2 className="font-display text-[15px] font-semibold text-[var(--color-ink)]">
          Changes{scenarioName ? ` in ${scenarioName}` : ''}
        </h2>
        <button
          type="button"
          aria-label="Close the table"
          onClick={() => setChangesTableOpen(false)}
          className="ml-auto rounded-md p-1.5 text-[var(--color-ink-3)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]"
        >
          <X size={16} />
        </button>
      </div>

      <div className="min-h-0 flex-1">
        <DataTable
          table="changes"
          columns={changeColumns()}
          rows={rows}
          rowKey={(change) => change.id}
          filename={`changes-${scenarioName ? slug(scenarioName) : 'plan'}`}
          onRowClick={(change) => {
            const personId = changePersonId(change);
            if (!personId) return;
            select(personId);
            openPanel('details');
            revealPerson(personId);
          }}
          empty={{
            title: 'Nothing to show',
            hint: 'Either the plan is empty, or every category is switched off below.',
          }}
          toolbar={
            present.length > 1 ? (
              <div role="group" aria-label="Categories" className="flex flex-wrap items-center gap-1">
                {present.map((category) => {
                  const on = chosen === null || chosen.has(category.kind);
                  return (
                    <button
                      key={category.kind}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggle(category.kind)}
                      className={cn(
                        'rounded-full border px-2 py-0.5 text-[11.5px] font-medium transition-colors',
                        on
                          ? 'border-[var(--color-brand)] bg-[var(--color-brand)]/10 text-[var(--color-brand)]'
                          : 'border-[var(--color-line)] text-[var(--color-ink-3)] hover:bg-[var(--color-surface-2)]',
                      )}
                    >
                      {category.label}
                    </button>
                  );
                })}
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
}

function slug(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'plan'
  );
}

/**
 * One row per change, with the old value beside the new one.
 *
 * From and To are deliberately generic. Every kind of change is a thing that was
 * one way and is now another, and a table with a column per kind would be mostly
 * empty — so the pair is filled in by whatever the kind's own fields mean, and
 * the reader gets one column to compare down.
 */
export function changeColumns(): ColumnDef<Change>[] {
  return [
    { key: 'category', label: 'Category', value: (change) => labelFor(change.kind) },
    { key: 'who', label: 'Who', value: (change) => subjectOf(change) },
    {
      key: 'title',
      label: 'Title',
      value: (change) => (isSquadChange(change) ? null : change.personTitle),
    },
    { key: 'from', label: 'From', value: (change) => fromOf(change) },
    { key: 'to', label: 'To', value: (change) => toOf(change) },

    // --- the columns a headcount plan is actually read for -----------------
    {
      key: 'reasonKind',
      label: 'Net new / backfill',
      value: (change) =>
        change.kind === 'hire' && change.reason
          ? change.reason.kind === 'backfill'
            ? 'Backfill'
            : 'Net new'
          : null,
    },
    {
      key: 'reason',
      label: 'Justification / backfilling',
      value: (change) =>
        change.kind === 'hire' && change.reason
          ? change.reason.kind === 'backfill'
            ? change.reason.backfillFor
            : change.reason.justification
          : null,
    },
    {
      key: 'startDate',
      label: 'Start date',
      value: (change) => (change.kind === 'hire' ? change.startDate : null),
    },
    {
      key: 'budget',
      label: 'Budget',
      align: 'right',
      /*
       * Off to start with, like every other salary figure in the app. A plan
       * gets opened in meetings and on shared screens, and a column of numbers
       * that is on the page whether or not anybody meant to show it will
       * eventually be shown to the wrong room.
       */
      hiddenByDefault: true,
      secret: true,
      value: (change) => (change.kind === 'hire' && change.cost ? formatMoney(change.cost) : null),
    },
    {
      key: 'budgetUsd',
      label: 'Budget (approx USD)',
      align: 'right',
      hiddenByDefault: true,
      secret: true,
      /*
       * Converted at the hand-maintained rates in core, and rounded: this is a
       * column somebody adds up, and a plan is decided weeks before anybody is
       * hired, so the false precision of a live rate would be worse than the
       * approximation.
       */
      value: (change) => {
        if (change.kind !== 'hire' || !change.cost) return null;
        const usd = approximateUsd(change.cost);
        return usd === null ? null : Math.round(usd);
      },
    },
    {
      key: 'department',
      label: 'Department',
      value: (change) => (change.kind === 'hire' ? change.department : null),
      hiddenByDefault: true,
    },
    {
      key: 'manager',
      label: 'Manager',
      value: (change) =>
        change.kind === 'hire' ? change.managerName : change.kind === 'move' ? change.toManagerName : null,
      hiddenByDefault: true,
    },
    {
      key: 'movedWith',
      label: 'Moved with',
      align: 'right',
      value: (change) => (change.kind === 'move' ? change.movedWith : null),
      hiddenByDefault: true,
    },
    { key: 'id', label: 'Change id', value: (change) => change.id, hiddenByDefault: true },
  ];
}

function labelFor(kind: ChangeKind): string {
  return CATEGORIES.find((category) => category.kind === kind)?.label ?? kind;
}

/** Who or what the change is about: a person, or a team when it has no person. */
function subjectOf(change: Change): string {
  if (change.kind === 'squad-member') return change.personName;
  if (isSquadChange(change)) return change.squadName;
  return change.personName;
}

function fromOf(change: Change): string | null {
  switch (change.kind) {
    case 'move':
      return change.fromManagerName ?? 'no manager';
    case 'retitle':
      return change.fromTitle;
    case 'department':
      return change.fromDepartment ?? 'none';
    case 'location':
      return change.fromLocation ?? 'nowhere';
    case 'dotted':
      // Everything it says is in `To`: the list after the change is the claim,
      // and where it came from is almost always "nobody".
      return null;
    case 'departure':
      return 'Here';
    case 'hire':
      return null;
    case 'squad-rename':
      return change.fromName;
    case 'squad-parent':
      return change.fromParentName ?? 'top level';
    case 'squad-member':
      return change.fromRole ?? 'not on it';
    default:
      return null;
  }
}

function toOf(change: Change): string | null {
  switch (change.kind) {
    case 'move':
      return change.toManagerName ?? 'no manager';
    case 'retitle':
      return change.toTitle;
    case 'department':
      return change.toDepartment ?? 'none';
    case 'location':
      return change.toLocation ?? 'nowhere';
    case 'dotted':
      return change.toNames.join(', ') || 'nobody';
    case 'departure':
      return 'Leaving';
    case 'hire':
      return change.personTitle;
    case 'squad-add':
      return `${change.memberCount} member(s)`;
    case 'squad-rename':
      return change.toName;
    case 'squad-parent':
      return change.toParentName ?? 'top level';
    case 'squad-disband':
      return 'Stood down';
    case 'squad-member':
      return change.toRole ?? 'off the team';
    default:
      return null;
  }
}
